import { useCallback, useState } from 'react';
import { useStore } from 'react-redux';
import { createFetchRetry } from '@rsos/capstone';
import { Button } from '@rsos/components/capstone/base';
import { useFlag } from '@rsos/flags';
import { useCurrentPSAP } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { getAPIHost } from '@rsos/utils/metaTags';

interface StatusState {
  type: 'loading' | 'success' | 'error';
  message: string;
}

/**
 * Test component for ETL integrations endpoints.
 * Only visible when air_ambulance_etl_integrations feature flag is enabled.
 * Provides buttons to test creating transports, updating statuses, and fetching data.
 */
export default function AirAmbulanceEtlTest(): JSX.Element | null {
  const isEtlTestEnabled = useFlag('air_ambulance_etl_integrations');
  const currentPSAP = useCurrentPSAP();
  const orgName = useCurrentOrgName();
  const store = useStore();

  const [status, setStatus] = useState<StatusState | null>(null);
  const [lastCreatedTripId, setLastCreatedTripId] = useState<string | null>(
    null,
  );

  if (!currentPSAP.account_id) {
    throw new Error('Current PSAP account ID is not available.');
  }

  const handleCreateTransport = useCallback(async () => {
    setStatus({ type: 'loading', message: 'Creating transport...' });
    try {
      const tripId = generateTripId();
      const payload = {
        systemId: 'unite-etl-test',
        extSystemTripId: tripId,
        isTest: true,
        requestor: {
          facilityId: currentPSAP.account_id,
        },
      };

      if (!orgName) {
        throw new Error("Current user's organization name not available");
      }
      const fetchRetry = createFetchRetry(store);
      const response = await fetchRetry(
        `${getAPIHost()}/v1/etl-integrations/internal/gmr/transports`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-RapidSOS-Org': orgName,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // eslint-disable-next-line no-console
      console.debug('Created Transport:', data);
      setLastCreatedTripId(tripId);
      setStatus({
        type: 'success',
        message: `Created! Trip ID: ${tripId}`,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Create Transport Error:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      setStatus({ type: 'error', message: `Error: ${errorMessage}` });
    }
  }, [currentPSAP, store, orgName]);

  const handleUpdateStatus = useCallback(async () => {
    if (!lastCreatedTripId) {
      setStatus({
        type: 'error',
        message: 'No transport created yet. Create one first!',
      });

      return;
    }

    setStatus({ type: 'loading', message: 'Updating status...' });
    try {
      const payload = {
        extSystemTripId: lastCreatedTripId,
        isTest: true,
        update: {
          status: {
            type: 'accepted',
            display: `Test Status Update - ${new Date().toISOString()}`,
            statusTimeUTC: new Date().toISOString(),
          },
        },
      };

      if (!orgName) {
        throw new Error("Current user's organization name not available");
      }

      const fetchRetry = createFetchRetry(store);
      const response = await fetchRetry(
        `${getAPIHost()}/v1/etl-integrations/ingress/gmr/statuses`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-RapidSOS-Source': 'air_ambulance',
            'X-RapidSOS-Org': orgName,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // eslint-disable-next-line no-console
      console.debug('Status Update Response:', data);
      setStatus({
        type: 'success',
        message: `Status updated for ${lastCreatedTripId}`,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Update Status Error:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      setStatus({ type: 'error', message: `Error: ${errorMessage}` });
    }
  }, [lastCreatedTripId, store, orgName]);

  const handleGetTransports = useCallback(async () => {
    setStatus({ type: 'loading', message: 'Fetching transports...' });
    try {
      const fetchRetry = createFetchRetry(store);
      const response = await fetchRetry(
        `${getAPIHost()}/v1/etl-integrations/api/gmr/transports`,
        {
          method: 'GET',
          headers: {
            'X-RapidSOS-Org': orgName,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // TODO: use zod or yup or something and add a schema for these payloads once we're beyond testing.

      const data = await response.json();
      // eslint-disable-next-line no-console
      console.debug('ETL Transports Response:', data);
      setStatus({
        type: 'success',
        message: `Found ${data.transports?.length || 0} transports (see console)`,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Get Transports Error:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      setStatus({ type: 'error', message: `Error: ${errorMessage}` });
    }
  }, [store, orgName]);

  // Don't render if feature flag is not enabled
  if (!isEtlTestEnabled) {
    return null;
  }

  return (
    <div
      style={{
        padding: 12,
        marginTop: 8,
        backgroundColor: '#f5f5f5',
        borderRadius: 4,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>
        ETL Integration Test Panel
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Button
            btnType="secondary"
            size="small"
            onClick={handleCreateTransport}
            disabled={status?.type === 'loading'}
          >
            1. Create Transport
          </Button>

          <Button
            btnType="secondary"
            size="small"
            onClick={handleUpdateStatus}
            disabled={status?.type === 'loading' || !lastCreatedTripId}
          >
            2. Update Status
          </Button>

          <Button
            btnType="secondary"
            size="small"
            onClick={handleGetTransports}
            disabled={status?.type === 'loading'}
          >
            3. Get Transports
          </Button>
        </div>

        {lastCreatedTripId && (
          <span style={{ fontSize: 11 }}>
            Last Trip ID: {lastCreatedTripId}
          </span>
        )}

        {status && <span style={{ fontSize: 11 }}>{status.message}</span>}
      </div>
    </div>
  );
}

function generateTripId(): string {
  return `test-trip-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}
